/**
 * Typography System - Swiss Design
 *
 * Clear hierarchy and readability
 *
 * STANDARD MODE: Ottimizzato per uso normale in ufficio
 * STRESS MODE: Font più grandi (+20-30%), line-height aumentato, contrasti migliorati
 * MOBILE MODE: Ottimizzato per schermi mobili (14-20px)
 */

// ============================================================================
// STANDARD MODE - Uso normale in ufficio
// ============================================================================

export const SWISS_TYPOGRAPHY = {
  /** Label - uppercase tracking (Standard: 12px - WCAG compliant) */
  label: 'text-xs font-medium tracking-[0.05em] uppercase text-gray-500',

  /** Label dark variant */
  labelDark: 'text-xs font-medium tracking-[0.05em] uppercase text-gray-900',

  /** Small label (11px - use sparingly, minimum accessible size) */
  labelSmall: 'text-[11px] font-medium tracking-[0.05em] uppercase text-gray-500',

  /** Display heading - extra large */
  displayXL: 'text-5xl font-bold text-gray-900 leading-tight',

  /** Display heading - large */
  displayLG: 'text-4xl font-bold text-gray-900 leading-tight',

  /** Display heading - medium */
  displayMD: 'text-3xl font-bold text-gray-900 leading-snug',

  /** Heading - large */
  headingLG: 'text-2xl font-bold text-gray-900 leading-snug',

  /** Heading - medium (24px - improved from 20px) */
  headingMD: 'text-2xl font-semibold text-gray-700 leading-snug',

  /** Heading - small (20px - improved from 18px) */
  headingSM: 'text-xl font-semibold text-gray-700 leading-snug',

  /** Body text - large */
  bodyLG: 'text-lg text-gray-600 leading-relaxed',

  /** Body text - standard (16px - WCAG AAA, improved from 14px) */
  body: 'text-base text-gray-600 leading-relaxed',

  /** Body text - small (14px - use for secondary content) */
  bodySM: 'text-sm text-gray-500 leading-relaxed',

  /** Body text - extra small (12px - minimal use) */
  bodyXS: 'text-xs text-gray-500',

  // ============================================================================
  // STRESS MODE - Uso sotto pressione/stress
  // ============================================================================

  /** Display stress - extra large numbers/values (25% larger) */
  displayStress: 'text-7xl font-bold text-gray-900 leading-none tracking-tight',

  /** Label stress - più leggibile sotto stress (14px, +17%) */
  labelStress: 'text-sm font-bold tracking-[0.05em] uppercase text-gray-600',

  /** Heading XL stress */
  headingXLStress: 'text-5xl font-bold text-gray-900 leading-tight',

  /** Heading LG stress */
  headingLGStress: 'text-4xl font-bold text-gray-900 leading-tight',

  /** Heading MD stress - titoli modali (30px, +25%) */
  headingMDStress: 'text-3xl font-bold text-gray-900 leading-tight',

  /** Heading SM stress */
  headingSMStress: 'text-2xl font-bold text-gray-900 leading-snug',

  /** Body LG stress */
  bodyLGStress: 'text-xl text-gray-700 leading-relaxed',

  /** Body stress - testo standard sotto stress (18px, +12%) */
  bodyStress: 'text-lg text-gray-700 leading-relaxed',

  /** Body SM stress - testo secondario sotto stress (16px) */
  bodySMStress: 'text-base text-gray-600 leading-relaxed',
} as const;

// ============================================================================
// MOBILE MODE - Per PWA e dispositivi mobili
// ============================================================================

/**
 * Mobile-optimized typography
 * 14-20px range with high readability
 */
export const SWISS_TYPOGRAPHY_MOBILE = {
  /** Label - uppercase tracking for categories */
  label: 'text-xs font-bold tracking-[0.05em] uppercase text-gray-600',

  /** Label dark variant */
  labelDark: 'text-xs font-bold tracking-[0.05em] uppercase text-gray-900',

  /** Heading - large (20px) */
  headingLG: 'text-xl font-bold text-gray-900 leading-tight',

  /** Heading - medium (18px) */
  headingMD: 'text-lg font-bold text-gray-900 leading-tight',

  /** Heading - small (16px) */
  headingSM: 'text-base font-bold text-gray-900 leading-snug',

  /** Body - large (16px) */
  bodyLG: 'text-base text-gray-700 leading-relaxed',

  /** Body - standard (14px) */
  body: 'text-sm text-gray-700 leading-relaxed',

  /** Body - small (12px - only for tertiary info) */
  bodySM: 'text-xs text-gray-600 leading-relaxed',

  /** Display number - for counters/quantities (24px) */
  displayNumber: 'text-2xl font-bold text-gray-900 leading-none',
} as const;

export type SwissTypography = keyof typeof SWISS_TYPOGRAPHY;
export type SwissTypographyMobile = keyof typeof SWISS_TYPOGRAPHY_MOBILE;
